import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, MapPin, ArrowRight, Star, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ResultsPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      const [data, userData] = await Promise.all([
        api.checkEligibility(),
        api.getMe()
      ]);
      setResults(data);
      setUser(userData);
    } catch (error) {
      toast.error("Failed to load results");
    } finally {
      setIsLoading(false);
    }
  };

  // No plan yet -> send them to pricing (the old modal never opened: prop name mismatch).
  const goToPricing = () => {
    toast("Start your plan to unlock visa details and tracking.");
    navigate("/pricing");
  };

  const handleViewVisa = (result) => {
    if (!user?.has_core_access) {
      goToPricing();
      return;
    }
    navigate(`/visa/${result.country_id}/${result.visa_type}`);
  };

  const handleAddCountry = async (result) => {
    if (!user?.has_core_access) {
      goToPricing();
      return;
    }
    try {
      await api.addUserCountry({ 
        country_id: result.country_id, 
        visa_id: result.visa_id 
      });
      toast.success(`Added ${result.country} to your roadmap!`);
    } catch (error) {
      if (error.message.includes("limit")) {
        toast.error("Your plan tracks up to 3 countries at a time. Remove one from your dashboard to add another.");
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleGoToDashboard = () => {
    if (!user?.has_core_access) {
      goToPricing();
      return;
    }
    navigate("/dashboard");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={handleGoToDashboard}>Dashboard</Button>
            <Button onClick={() => navigate("/profile")}>My Profile</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-['Manrope'] mb-4">Your Custom Visa Roadmap</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            We found {results.length} visa options that match your profile. 
            Upgrade to view detailed requirements and start tracking your application.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {results.map((result, index) => (
            <Card key={index} className="overflow-hidden border-border/50 hover:shadow-lg transition-all animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-3xl">{result.flag_emoji}</span>
                  <Badge variant={result.match_score > 80 ? "default" : "secondary"} className="rounded-full">
                    {result.match_score}% Match
                  </Badge>
                </div>
                <CardTitle className="text-xl">{result.country}</CardTitle>
                <CardDescription className="font-medium text-primary">{result.visa_type}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Star className="h-4 w-4 text-amber-500" />
                    <span>{result.reason}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Processing: {result.timeline_months}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4" />
                    <span>Difficulty: {result.difficulty}</span>
                  </div>
                </div>
                
                <div className="pt-4 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => handleAddCountry(result)}>Track</Button>
                  <Button className="flex-1" onClick={() => handleViewVisa(result)}>
                    Details
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {results.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-sm">
              <MapPin className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No exact matches found</h2>
            <p className="text-muted-foreground mb-8">Try adjusting your profile or regions to see more results.</p>
            <Button size="lg" onClick={() => navigate("/onboarding")}>Update Profile</Button>
          </div>
        )}
      </main>

    </div>
  );
};

export default ResultsPage;
